import { useEffect } from 'react';

// ---- Tunable constants ----
const SPLASH_VISIBLE_MS = 7000;
const SPLASH_FADE_MS = 800;
const PARTICLE_COUNT = 12;
const STAGGER_STEP_MS = 120;
const PARALLAX_HERO_FACTOR = 0.2;
const OBSERVER_THRESHOLD = 0;
const OBSERVER_ROOT_MARGIN = '0px 0px -120px 0px';

// ---- Pure DOM helpers ----
const buildElement = (tag, className, text) => {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
};

const createSplashScreen = () => {
  const splash = buildElement('div', 'splash-screen');
  const splashContent = buildElement('div', 'splash-content');
  const splashLogo = buildElement('div', 'splash-logo');
  const splashTitle = buildElement('h1', 'splash-title');
  const splashPulso = buildElement('span', 'splash-pulso', 'Pulso');
  const splashMedia = buildElement('span', 'splash-media', 'Media Agency');
  const splashAuthor = buildElement('span', 'splash-author', 'by Pau Asensio');
  const splashLoader = buildElement('div', 'splash-loader');
  const loaderBar = buildElement('div', 'loader-bar');

  splashLoader.appendChild(loaderBar);
  splashTitle.appendChild(splashPulso);
  splashTitle.appendChild(splashMedia);
  splashTitle.appendChild(splashAuthor);
  splashLogo.appendChild(splashTitle);
  splashLogo.appendChild(splashLoader);
  splashContent.appendChild(splashLogo);
  splash.appendChild(splashContent);
  document.body.appendChild(splash);
  document.body.style.overflow = 'hidden';

  setTimeout(() => {
    splash.classList.add('splash-fade-out');
    setTimeout(() => {
      splash.remove();
      document.body.style.overflow = '';
      document.body.classList.add('page-loaded');
    }, SPLASH_FADE_MS);
  }, SPLASH_VISIBLE_MS);
};

const createParticles = () => {
  const container = buildElement('div', 'particle-container');
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const p = buildElement('div', 'floating-particle-enhanced');
    p.style.left = Math.random() * 100 + '%';
    p.style.top = Math.random() * 100 + '%';
    p.style.animationDelay = Math.random() * 5 + 's';
    p.style.animationDuration = 10 + Math.random() * 10 + 's';
    container.appendChild(p);
  }
  document.body.appendChild(container);
};

// Only applies to the hero background, not content images while reading
const handleHeroParallax = () => {
  const scrolled = window.pageYOffset;
  const hero = document.querySelector('#inicio');
  if (!hero) return;
  const heroContent = hero.querySelector('.relative.max-w-7xl');
  if (heroContent) {
    heroContent.style.transform = `translateY(${scrolled * PARALLAX_HERO_FACTOR}px)`;
  }
};

const ensureProgressBar = () => {
  let bar = document.querySelector('.loading-bar');
  if (!bar) {
    bar = buildElement('div', 'loading-bar');
    document.body.appendChild(bar);
  }
  return bar;
};

const updateScrollProgress = () => {
  const scrollTop = window.pageYOffset;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  const scrollPercent = (scrollTop / docHeight) * 100;
  ensureProgressBar().style.width = `${scrollPercent}%`;
};

// Section-level entrance animations (dynamism BETWEEN sections)
const observeAnimatedElements = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('revealed');

        // Stagger children of stagger-list once parent is revealed
        if (entry.target.classList.contains('stagger-list')) {
          const children = entry.target.children;
          Array.from(children).forEach((child, index) => {
            setTimeout(() => child.classList.add('revealed'), index * STAGGER_STEP_MS);
          });
        }
      });
    },
    { threshold: OBSERVER_THRESHOLD, rootMargin: OBSERVER_ROOT_MARGIN }
  );

  const selectors = [
    '.scroll-reveal',
    '.scroll-reveal-left',
    '.scroll-reveal-right',
    '.scroll-reveal-scale',
    '.zoom-in',
    '.rotate-in',
    '.bounce-in',
    '.stagger-list',
    '.curtain-section',
  ];
  document.querySelectorAll(selectors.join(', ')).forEach((el) => observer.observe(el));

  return observer;
};

const enableSmoothAnchorScroll = () => {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
};

const ScrollEffects = () => {
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        handleHeroParallax();
        updateScrollProgress();
        ticking = false;
      });
      ticking = true;
    };

    const observer = observeAnimatedElements();
    enableSmoothAnchorScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    createSplashScreen();
    createParticles();
    updateScrollProgress();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      ['.loading-bar', '.particle-container'].forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
};

export default ScrollEffects;
